import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticBlowerFan } from "./realistic";

/**
 * BlowerFanNode – Blower/fan visualization.
 * Supports:
 * - Realistic 3D SCADA design with rotating centrifugal blades & exhaust wind
 * - Glassmorphism design
 * - Classic industrial SVG layout
 */
const BlowerFanNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticBlowerFan
              liveValue={liveValue}
              unit={unit || "RPM"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              speedScale={speedScale}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const strokeColor = isRunning ? color : "#94a3b8";
        const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";
        // Clamp to 0.5s minimum (≈120 RPM) so the fan never spins unrealistically
        // fast when maxLevel is misconfigured or the live value exceeds it.
        const spinDuration = isRunning ? `${Math.max(0.5, 0.8 / (speedScale || 1))}s` : "0s";

        const showOldDesign = designType === "new_design" || designType === "classic";

        return (
          <svg
            width="80"
            height="88"
            viewBox="0 0 80 88"
            fill="none"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  <linearGradient id="fanGlassGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="fanActiveGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={color} stopOpacity="1" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.5" />
                  </linearGradient>
                  <filter id="fanGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Housing circle */}
                <circle
                  cx="40"
                  cy="40"
                  r="34"
                  fill="url(#fanGlassGrad)"
                  stroke={isRunning ? "url(#fanActiveGrad)" : "#475569"}
                  strokeWidth="2.5"
                  style={{
                    filter: isRunning ? "url(#fanGlow)" : "none",
                    transition: "all 0.3s ease",
                  }}
                />

                {/* Fan blades – 6 blades (thicker and more opaque) */}
                <g
                  style={{
                    transformOrigin: "40px 40px",
                    animation: isRunning ? `live-pump-spin ${spinDuration} linear infinite` : "none",
                  }}
                >
                  {[0, 60, 120, 180, 240, 300].map((angle) => (
                    <ellipse
                      key={angle}
                      cx="40"
                      cy="22"
                      rx="7.5"
                      ry="16"
                      fill={isRunning ? "url(#fanActiveGrad)" : "#64748b"}
                      opacity="0.8"
                      stroke={strokeColor}
                      strokeWidth="1.5"
                      transform={`rotate(${angle} 40 40)`}
                    />
                  ))}
                </g>

                {/* Center hub */}
                <circle
                  cx="40"
                  cy="40"
                  r="8"
                  fill={isRunning ? "url(#fanActiveGrad)" : "#64748b"}
                  style={{ filter: isRunning ? "url(#fanGlow)" : "none" }}
                />
                <circle cx="40" cy="40" r="3" fill="#fff" opacity="0.6" />

                {/* High-visibility Wind/airflow waves exiting blower */}
                {isRunning && (
                  <g filter="url(#fanGlow)">
                    <path d="M 68,30 C 76,26 76,44 84,40" stroke={color} strokeWidth="3" strokeDasharray="5 5" fill="none">
                      <animate attributeName="stroke-dashoffset" values="10;0" dur="0.8s" repeatCount="indefinite" />
                    </path>
                    <path d="M 68,44 C 76,40 76,58 84,54" stroke={color} strokeWidth="2" strokeDasharray="5 5" fill="none">
                      <animate attributeName="stroke-dashoffset" values="10;0" dur="1s" repeatCount="indefinite" />
                    </path>
                  </g>
                )}

                {/* Premium status dot */}
                <circle cx="68" cy="12" r="5.5" fill={dotColor} stroke="#ffffff" strokeWidth="2" filter={isRunning ? "url(#fanGlow)" : "none"}>
                  {isRunning && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                  )}
                </circle>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* Housing circle */}
                <circle cx="40" cy="40" r="34" fill={`${color}08`} stroke={strokeColor} strokeWidth="2" />

                {/* Fan blades – 6 blades */}
                <g style={{ transformOrigin: "40px 40px", animation: isRunning ? `live-pump-spin ${spinDuration} linear infinite` : "none" }}>
                  {[0, 60, 120, 180, 240, 300].map((angle) => (
                    <ellipse
                      key={angle}
                      cx="40"
                      cy="22"
                      rx="5"
                      ry="14"
                      fill={`${color}30`}
                      stroke={color}
                      strokeWidth="1"
                      transform={`rotate(${angle} 40 40)`}
                    />
                  ))}
                </g>

                {/* Center hub */}
                <circle cx="40" cy="40" r="7" fill={isRunning ? color : "#94a3b8"} />
                <circle cx="40" cy="40" r="3" fill="#fff" opacity="0.4" />

                {/* Wind/airflow indicators */}
                {isRunning && (
                  <g opacity="0.3" style={{ animation: "live-ahu-airflow 1.5s linear infinite" }}>
                    <path d="M74 35 Q80 40 74 45" stroke={color} strokeWidth="1.5" fill="none" />
                    <path d="M76 33 Q84 40 76 47" stroke={color} strokeWidth="1" fill="none" />
                  </g>
                )}

                {/* Status dot */}
                <circle cx="68" cy="12" r="4" fill={dotColor} />

                {/* Inlet */}
                <rect x="0" y="36" width="6" height="8" fill={color} rx="2" />
              </>
            )}

            {/* Status text - clearly below fan circle */}
            <text x="40" y="84" textAnchor="middle" fontSize="7.5" fontWeight="800" fill={dotColor} style={{ letterSpacing: 0.5 }}>
              {statusColor === "green" ? "RUNNING" : statusColor === "red" ? "STOPPED" : "OFFLINE"}
            </text>
          </svg>
        );
      }}
    </BaseNode>
  );
};

BlowerFanNode.propTypes = { data: PropTypes.object, selected: PropTypes.bool, id: PropTypes.string };
export default BlowerFanNode;
